import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { LockOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { financialIntegrationsService } from '../services/api';

const { Title, Text } = Typography;

const isBlank = (value) => value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

const isEntitlementBlockedError = (error) =>
  error?.response?.status === 403 &&
  error?.response?.data?.detail?.code === 'SAAS_ENTITLEMENT_BLOCKED';

const normalizeEnvironment = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'sandbox' || normalized === 'production') {
    return normalized;
  }
  return null;
};

const environmentTag = (environment) => {
  if (environment === 'sandbox') {
    return <Tag color="gold">SANDBOX</Tag>;
  }
  if (environment === 'production') {
    return <Tag color="red">PRODUÇÃO</Tag>;
  }
  return <Tag color="default">AMBIENTE NAO DEFINIDO</Tag>;
};

const environmentLabel = (environment) => {
  if (environment === 'sandbox') return 'sandbox';
  if (environment === 'production') return 'produção';
  return '-';
};

const normalizeOptions = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }
  return options
    .map((option) => {
      if (typeof option === 'string') {
        return { label: option, value: option };
      }
      if (option && typeof option === 'object' && option.value !== undefined) {
        return {
          label: option.label ?? String(option.value),
          value: option.value,
        };
      }
      return null;
    })
    .filter(Boolean);
};

const buildConfigDefaults = (provider, currentConfig) => {
  const configSchema = provider?.config_schema;
  const runtimeDefaults = provider?.runtime_defaults;

  const fromSchema = {};
  if (Array.isArray(configSchema?.fields)) {
    configSchema.fields.forEach((field) => {
      if (!field || typeof field !== 'object') {
        return;
      }
      if (field.default !== undefined) {
        fromSchema[String(field.key)] = field.default;
      }
    });
  }

  const fromRuntime = runtimeDefaults && typeof runtimeDefaults === 'object' ? runtimeDefaults : {};
  const current = currentConfig && typeof currentConfig === 'object' ? currentConfig : {};
  return {
    ...fromSchema,
    ...fromRuntime,
    ...current,
  };
};

const sourceTag = (source) => {
  if (source === 'unit_override') return <Tag color="blue">Override da unidade</Tag>;
  if (source === 'tenant_default') return <Tag color="green">Padrão do tenant</Tag>;
  return <Tag color="gold">Fallback ASAAS</Tag>;
};

const statusTag = (enabled, configured) => {
  if (!enabled) return <Tag color="default">Desabilitado</Tag>;
  return configured ? <Tag color="green">Configurado</Tag> : <Tag color="orange">Pendente de credencial</Tag>;
};

const renderMaskedCredentials = (masked) => {
  const entries = Object.entries(masked || {});
  if (!entries.length) {
    return <Text type="secondary">Sem credencial mascarada no retorno.</Text>;
  }

  return (
    <Space wrap>
      {entries.map(([key, value]) => (
        <Tag key={key} color="processing">
          {key}: {value || 'nao definido'}
        </Tag>
      ))}
    </Space>
  );
};

const FinancialSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [providers, setProviders] = useState([]);
  const [config, setConfig] = useState(null);
  const [entitlementBlocked, setEntitlementBlocked] = useState(false);
  const selectedProviderKey = Form.useWatch('provider_key', form);
  const credentialValues = Form.useWatch('credential_values', form) || {};

  const getCurrentConfigForProvider = (providerKey, currentConfig = config) => {
    if (
      !currentConfig?.tenant_default
      || String(currentConfig.tenant_default.provider_key || '') !== String(providerKey || '')
    ) {
      return {};
    }
    return currentConfig.tenant_default?.config && typeof currentConfig.tenant_default.config === 'object'
      ? currentConfig.tenant_default.config
      : {};
  };

  const isSelectedProviderConfigured = useMemo(() => {
    if (!selectedProviderKey || !config?.tenant_default) {
      return false;
    }
    const isSameProvider = String(config.tenant_default.provider_key || '') === String(selectedProviderKey);
    return isSameProvider && Boolean(config.tenant_default.configured);
  }, [selectedProviderKey, config]);

  const selectedProvider = useMemo(
    () => providers.find((provider) => String(provider.provider_key) === String(selectedProviderKey || '')) || null,
    [providers, selectedProviderKey]
  );

  const credentialFields = useMemo(() => {
    const fields = selectedProvider?.credential_schema?.fields;
    return Array.isArray(fields) ? fields : [];
  }, [selectedProvider]);

  const configFields = useMemo(() => {
    const fields = selectedProvider?.config_schema?.fields;
    return Array.isArray(fields) ? fields : [];
  }, [selectedProvider]);

  const selectedProviderCapabilities = useMemo(() => {
    return selectedProvider?.capabilities && typeof selectedProvider.capabilities === 'object'
      ? selectedProvider.capabilities
      : {};
  }, [selectedProvider]);

  const selectedProviderMaskedCredentials = useMemo(() => {
    if (!config?.tenant_default || !selectedProviderKey) {
      return {};
    }
    const isSameProvider = String(config.tenant_default.provider_key || '') === String(selectedProviderKey);
    if (!isSameProvider) {
      return {};
    }
    return config.tenant_default.credentials_masked && typeof config.tenant_default.credentials_masked === 'object'
      ? config.tenant_default.credentials_masked
      : {};
  }, [config, selectedProviderKey]);

  const savedEnvironment = useMemo(() => {
    const tenantDefaultConfig = config?.tenant_default?.config;
    if (!tenantDefaultConfig || typeof tenantDefaultConfig !== 'object') {
      return null;
    }
    return normalizeEnvironment(tenantDefaultConfig.environment);
  }, [config]);

  const hasTemporarySecretCredential = useMemo(() => {
    if (isSelectedProviderConfigured || !credentialValues || typeof credentialValues !== 'object') {
      return false;
    }

    return credentialFields.some((field) => {
      if (!field || typeof field !== 'object') {
        return false;
      }
      const fieldType = String(field.type || 'text').toLowerCase();
      const isSecret = Boolean(field.secret) || fieldType === 'password';
      if (!isSecret) {
        return false;
      }
      const fieldKey = String(field.key || '');
      if (!fieldKey) {
        return false;
      }
      return !isBlank(credentialValues[fieldKey]);
    });
  }, [isSelectedProviderConfigured, credentialValues, credentialFields]);

  const credentialStatusTagNode = useMemo(() => {
    if (isSelectedProviderConfigured) {
      return <Tag color="green">Credencial configurada</Tag>;
    }
    if (hasTemporarySecretCredential) {
      return <Tag color="blue">Credencial temporaria preenchida</Tag>;
    }
    return <Tag color="orange">Credencial nao configurada</Tag>;
  }, [isSelectedProviderConfigured, hasTemporarySecretCredential]);

  const applyProviderFormState = (providerKey, providerList = providers, configData = config) => {
    const provider = providerList.find((item) => String(item.provider_key) === String(providerKey || '')) || null;
    const currentConfig = getCurrentConfigForProvider(providerKey, configData);

    form.setFieldsValue({
      provider_key: providerKey,
      credential_values: {},
      config_values: buildConfigDefaults(provider, currentConfig),
    });
  };

  const loadData = async () => {
    setLoading(true);
    setEntitlementBlocked(false);
    try {
      const [providersRes, configRes] = await Promise.all([
        financialIntegrationsService.listProviders(),
        financialIntegrationsService.getConfig(),
      ]);

      const providerItems = Array.isArray(providersRes?.data?.items) ? providersRes.data.items : [];
      const configData = configRes?.data || null;
      setProviders(providerItems);
      setConfig(configData);

      const initialProviderKey = configData?.tenant_default?.provider_key || providerItems[0]?.provider_key;

      form.setFieldsValue({
        provider_key: initialProviderKey,
        enabled: Boolean(configData?.tenant_default?.enabled ?? true),
        credential_values: {},
        config_values: {},
      });

      applyProviderFormState(initialProviderKey, providerItems, configData);
    } catch (error) {
      if (isEntitlementBlockedError(error)) {
        setEntitlementBlocked(true);
      } else {
        const detail = error?.response?.data?.detail;
        message.error(typeof detail === 'string' ? detail : 'Erro ao carregar configuração financeira');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const providerOptions = useMemo(
    () => providers.map((provider) => ({ value: provider.provider_key, label: provider.display_name })),
    [providers]
  );

  const unidadesColumns = [
    {
      title: 'Unidade',
      dataIndex: 'unidade_nome',
      key: 'unidade_nome',
      render: (value, record) => value || `ID ${record.unidade_id}`,
    },
    {
      title: 'Provider efetivo',
      dataIndex: 'effective_provider_display_name',
      key: 'effective_provider_display_name',
    },
    {
      title: 'Origem',
      dataIndex: 'source',
      key: 'source',
      render: (value) => sourceTag(value),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => statusTag(Boolean(record.enabled), Boolean(record.configured)),
    },
  ];

  const handleSave = async (values) => {
    const provider = providers.find((item) => String(item.provider_key) === String(values.provider_key || '')) || null;
    const currentProviderConfig = getCurrentConfigForProvider(values.provider_key);
    const formConfigValues = values.config_values && typeof values.config_values === 'object' ? values.config_values : {};

    const payloadConfig = {
      ...currentProviderConfig,
    };

    configFields.forEach((field) => {
      const fieldKey = String(field?.key || '');
      if (!fieldKey) {
        return;
      }
      const value = formConfigValues[fieldKey];
      if (value === undefined) {
        return;
      }
      if (typeof value === 'string' && value.trim() === '') {
        return;
      }
      payloadConfig[fieldKey] = value;
    });

    const formCredentialValues = values.credential_values && typeof values.credential_values === 'object'
      ? values.credential_values
      : {};
    const payloadCredentials = {};

    const activeCredentialFields = Array.isArray(provider?.credential_schema?.fields)
      ? provider.credential_schema.fields
      : [];

    activeCredentialFields.forEach((field) => {
      const fieldKey = String(field?.key || '');
      if (!fieldKey) {
        return;
      }
      const value = formCredentialValues[fieldKey];
      if (isBlank(value)) {
        return;
      }
      payloadCredentials[fieldKey] = typeof value === 'string' ? value.trim() : value;
    });

    const payload = {
      provider_key: values.provider_key,
      enabled: Boolean(values.enabled),
      config: payloadConfig,
    };

    if (Object.keys(payloadCredentials).length > 0) {
      payload.credentials = payloadCredentials;
    }

    setSaving(true);
    try {
      await financialIntegrationsService.updateTenantDefault(payload);
      message.success('Configuração financeira salva com sucesso');
      form.setFieldValue('credential_values', {});
      setConnectionResult(null);
      await loadData();
    } catch (error) {
      if (isEntitlementBlockedError(error)) {
        setEntitlementBlocked(true);
      } else {
        const detail = error?.response?.data?.detail;
        message.error(typeof detail === 'string' ? detail : 'Erro ao salvar configuração financeira');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    const values = form.getFieldsValue(true);
    const provider = providers.find((item) => String(item.provider_key) === String(values.provider_key || '')) || null;
    const formConfigValues = values.config_values && typeof values.config_values === 'object' ? values.config_values : {};
    const formCredentialValues = values.credential_values && typeof values.credential_values === 'object'
      ? values.credential_values
      : {};

    const payloadConfig = {
      ...buildConfigDefaults(provider, getCurrentConfigForProvider(values.provider_key)),
    };
    configFields.forEach((field) => {
      const fieldKey = String(field?.key || '');
      if (!fieldKey) {
        return;
      }
      const value = formConfigValues[fieldKey];
      if (value === undefined) {
        return;
      }
      payloadConfig[fieldKey] = value;
    });

    const payloadCredentials = {};
    const activeCredentialFields = Array.isArray(provider?.credential_schema?.fields)
      ? provider.credential_schema.fields
      : [];
    activeCredentialFields.forEach((field) => {
      const fieldKey = String(field?.key || '');
      if (!fieldKey) {
        return;
      }
      const value = formCredentialValues[fieldKey];
      if (isBlank(value)) {
        return;
      }
      payloadCredentials[fieldKey] = typeof value === 'string' ? value.trim() : value;
    });

    const payload = {
      provider_key: values.provider_key,
      config: payloadConfig,
    };

    const selectedEnvironmentForTest = normalizeEnvironment(payloadConfig.environment);

    if (Object.keys(payloadCredentials).length > 0) {
      payload.credentials = payloadCredentials;
    }

    setTestingConnection(true);
    setConnectionResult(null);
    try {
      const response = await financialIntegrationsService.testConnection(payload);
      const result = response?.data || {};
      const success = Boolean(result.success);
      const errorType = result?.diagnostics?.error_type;
      const alertType = success ? 'success' : (errorType === 'missing_credentials' ? 'warning' : 'error');
      setConnectionResult({
        type: alertType,
        success,
        message: result.message || (success ? 'Conexão bem-sucedida.' : 'Falha na conexão.'),
        diagnostics: result.diagnostics || {},
        source: result.source || '-',
        environment: normalizeEnvironment(result?.diagnostics?.environment) || selectedEnvironmentForTest,
      });
    } catch (error) {
      if (isEntitlementBlockedError(error)) {
        setEntitlementBlocked(true);
      } else {
        const detail = error?.response?.data?.detail;
        setConnectionResult({
          type: 'error',
          success: false,
          message: typeof detail === 'string' ? detail : 'Falha na conexão com o provider.',
          diagnostics: {},
          source: '-',
          environment: selectedEnvironmentForTest,
        });
      }
    } finally {
      setTestingConnection(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <Spin size="large" tip="Carregando configuração financeira..." />
      </div>
    );
  }

  if (entitlementBlocked) {
    return (
      <div style={{ padding: '24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
            <Title level={2} style={{ marginBottom: 0 }}>Configurações Financeiras</Title>
            <Text type="secondary">Defina o provider financeiro padrão do tenant para novas cobranças.</Text>
          </Col>
        </Row>
        <Alert
          type="warning"
          showIcon
          message="Módulo financeiro indisponível no seu plano atual."
          description="Este recurso não está incluído na assinatura do tenant. Solicite alteração de plano para liberar o financeiro."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
        <Col>
          <Title level={2} style={{ marginBottom: 0 }}>Configurações Financeiras</Title>
          <Text type="secondary">Defina o provider financeiro padrão do tenant para novas cobranças.</Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={loadData}>Recarregar</Button>
        </Col>
      </Row>

      <Alert
        type="warning"
        showIcon
        message="Alterações afetam novas cobranças geradas após a configuração. Cobranças já criadas não são alteradas automaticamente."
        style={{ marginBottom: '16px' }}
      />

      <Card title="Padrão atual do tenant" style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Text strong>Provider</Text>
            <div>{config?.tenant_default?.provider_display_name || '-'}</div>
          </Col>
          <Col xs={24} md={6}>
            <Text strong>Origem</Text>
            <div>{sourceTag(config?.tenant_default?.source)}</div>
          </Col>
          <Col xs={24} md={6}>
            <Text strong>Ambiente</Text>
            <div>{environmentTag(savedEnvironment)}</div>
          </Col>
          <Col xs={24} md={6}>
            <Text strong>Status</Text>
            <div>{statusTag(Boolean(config?.tenant_default?.enabled), Boolean(config?.tenant_default?.configured))}</div>
          </Col>
          <Col xs={24}>
            <Text strong>Credenciais (mascaradas)</Text>
            <div style={{ marginTop: '8px' }}>{renderMaskedCredentials(config?.tenant_default?.credentials_masked)}</div>
          </Col>
        </Row>
      </Card>

      <Card title="Atualizar provider padrão" style={{ marginBottom: '16px' }}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Provider padrão"
                name="provider_key"
                rules={[{ required: true, message: 'Selecione um provider' }]}
              >
                <Select
                  options={providerOptions}
                  placeholder="Selecione o provider"
                  onChange={(providerKey) => {
                    applyProviderFormState(providerKey);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Ativo" name="enabled" valuePropName="checked">
                <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
              </Form.Item>
            </Col>
          </Row>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Por segurança, credenciais salvas não são exibidas novamente."
          />

          {Object.keys(selectedProviderCapabilities).length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <Text strong>Capacidades do provider</Text>
              <div style={{ marginTop: 8 }}>
                <Space wrap>
                  {Object.entries(selectedProviderCapabilities).map(([capability, enabled]) => (
                    <Tag key={capability} color={enabled ? 'green' : 'default'}>
                      {capability}: {enabled ? 'sim' : 'nao'}
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          ) : null}

          {credentialFields.length > 0 ? (
            <>
              <Divider orientation="left">Credenciais</Divider>
              {credentialFields.map((field) => {
                const fieldKey = String(field?.key || '');
                const fieldLabel = field?.label || fieldKey;
                const fieldType = String(field?.type || 'text').toLowerCase();
                const required = Boolean(field?.required);
                const isSecret = Boolean(field?.secret) || fieldType === 'password';
                const options = normalizeOptions(field?.options);

                const rules = [];
                if (required) {
                  rules.push({
                    validator: async (_, value) => {
                      if (isSecret && isSelectedProviderConfigured && isBlank(value)) {
                        return;
                      }
                      if (isBlank(value)) {
                        throw new Error(`Preencha ${fieldLabel}`);
                      }
                    },
                  });
                }

                let inputNode = null;
                if (fieldType === 'password') {
                  inputNode = (
                    <Input.Password
                      autoComplete="new-password"
                      prefix={<LockOutlined />}
                      placeholder={field?.placeholder || 'Digite o valor'}
                    />
                  );
                } else if (fieldType === 'select') {
                  inputNode = <Select options={options} placeholder={field?.placeholder || 'Selecione'} />;
                } else if (fieldType === 'boolean') {
                  inputNode = <Switch checkedChildren="Sim" unCheckedChildren="Nao" />;
                } else {
                  inputNode = <Input placeholder={field?.placeholder || 'Digite o valor'} />;
                }

                return (
                  <Form.Item
                    key={`credential_${fieldKey}`}
                    label={fieldLabel}
                    name={['credential_values', fieldKey]}
                    rules={rules}
                    valuePropName={fieldType === 'boolean' ? 'checked' : 'value'}
                    extra={
                      isSecret
                        ? 'Se deixar em branco, a credencial atual será mantida.'
                        : field?.help
                    }
                  >
                    {inputNode}
                  </Form.Item>
                );
              })}

              {Object.keys(selectedProviderMaskedCredentials).length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Status de credenciais salvas</Text>
                  <div style={{ marginTop: 8 }}>
                    {credentialStatusTagNode}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    {renderMaskedCredentials(selectedProviderMaskedCredentials)}
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 16 }}>
                  {credentialStatusTagNode}
                </div>
              )}
            </>
          ) : null}

          {configFields.length > 0 ? (
            <>
              <Divider orientation="left">Configuração</Divider>
              {configFields.map((field) => {
                const fieldKey = String(field?.key || '');
                const fieldLabel = field?.label || fieldKey;
                const fieldType = String(field?.type || 'text').toLowerCase();
                const required = Boolean(field?.required);
                const options = normalizeOptions(field?.options);

                const rules = [];
                if (required) {
                  rules.push({ required: true, message: `Selecione/preencha ${fieldLabel}` });
                }

                if (fieldType === 'select') {
                  return (
                    <Form.Item
                      key={`config_${fieldKey}`}
                      label={fieldLabel}
                      name={['config_values', fieldKey]}
                      rules={rules}
                      extra={field?.help}
                    >
                      <Select options={options} placeholder={field?.placeholder || 'Selecione'} />
                    </Form.Item>
                  );
                }

                if (fieldType === 'boolean') {
                  return (
                    <Form.Item
                      key={`config_${fieldKey}`}
                      label={fieldLabel}
                      name={['config_values', fieldKey]}
                      rules={rules}
                      valuePropName="checked"
                      extra={field?.help}
                    >
                      <Switch checkedChildren="Sim" unCheckedChildren="Nao" />
                    </Form.Item>
                  );
                }

                if (fieldType === 'password') {
                  return (
                    <Form.Item
                      key={`config_${fieldKey}`}
                      label={fieldLabel}
                      name={['config_values', fieldKey]}
                      rules={rules}
                      extra={field?.help}
                    >
                      <Input.Password autoComplete="new-password" prefix={<LockOutlined />} />
                    </Form.Item>
                  );
                }

                return (
                  <Form.Item
                    key={`config_${fieldKey}`}
                    label={fieldLabel}
                    name={['config_values', fieldKey]}
                    rules={rules}
                    extra={field?.help}
                  >
                    <Input placeholder={field?.placeholder || 'Digite o valor'} />
                  </Form.Item>
                );
              })}
            </>
          ) : null}

          {connectionResult ? (
            <Alert
              style={{ marginBottom: 16 }}
              type={connectionResult.type}
              showIcon
              message={connectionResult.success ? 'Conexão bem-sucedida' : 'Falha na conexão'}
              description={
                <div>
                  <div>{connectionResult.message}</div>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Fonte da credencial: {connectionResult.source}</Text>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Ambiente: {environmentLabel(connectionResult.environment)}</Text>
                  </div>
                </div>
              }
            />
          ) : null}

          <Space>
            <Button onClick={handleTestConnection} loading={testingConnection}>
              Testar conexão
            </Button>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
              Salvar configuração
            </Button>
          </Space>
        </Form>
      </Card>

      <Card title="Unidades (somente leitura nesta fase)">
        <Table
          rowKey="unidade_id"
          dataSource={Array.isArray(config?.units) ? config.units : []}
          columns={unidadesColumns}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Nenhuma unidade encontrada' }}
        />
      </Card>
    </div>
  );
};

export default FinancialSettings;
